import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Home, Users, Briefcase, FileText, Star, Wallet, Settings, User, MessageCircle, Phone, Mail, Send, Video, TrendingUp, ArrowUp, Calendar, Clock, CheckCircle, Eye, Share2, Lock } from 'lucide-react';
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';
import BaseUrl from '../../ApiServices/BaseUrl';
import "./AgencyDashboard.css"

import { Modal, Button, Form, Spinner, Badge, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Footer from '../../Footer/Footer';

export default function GulfWorkerDashboard() {

  const navigate = useNavigate(); // Add this hook

  const [agencyData, setAgencyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add these state variables for share functionality
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareContent, setShareContent] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Add these state variables after the existing ones
  const [recentCVs, setRecentCVs] = useState([]);
  const [cvLoading, setCvLoading] = useState(true);
  const [cvError, setCvError] = useState(null);

  // Add this with other state variables
  const [activeCVs, setActiveCVs] = useState(0);
  const [activeCVsLoading, setActiveCVsLoading] = useState(true);


  const [planDetails, setPlanDetails] = useState(null);


  // Add this with other state variables
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [candidatesLoading, setCandidatesLoading] = useState(true);


  // Add this state variable after other state variables
  const [activeJobs, setActiveJobs] = useState(0);
  const [activeJobsLoading, setActiveJobsLoading] = useState(true);

  const [postingSub, setPostingSub] = useState(null);
  const [contactSub, setContactSub] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [topUpModal, setTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [processingTopUp, setProcessingTopUp] = useState(false);
  const [profileViewers, setProfileViewers] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(true);

  // Fetch active jobs for the agency
  const fetchActiveJobs = async () => {
    try {
      const agencyId = loggedInUser?.id;
      if (!agencyId) return;

      const response = await fetch(`${BaseUrl}/api/employer/by-user?user_id=${agencyId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch job data');
      }
      let data = await response.json();
      if (!Array.isArray(data)) data = data ? [data] : [];

      setActiveJobs(data.length);
    } catch (err) {
      console.error('Error fetching active jobs:', err);
      setActiveJobs(0);
    } finally {
      setActiveJobsLoading(false);
    }
  };


  const fetchTotalCandidates = async () => {
    try {
      const response = await fetch(`${BaseUrl}/`);
      if (!response.ok) {
        throw new Error('Failed to fetch users data');
      }
      const data = await response.json();

      // Count users where id matches logged-in user's id
      const count = data.filter(user => user.id === userId).length;
      setTotalCandidates(count);
    } catch (err) {
      console.error('Error fetching total candidates:', err);
      setTotalCandidates(0);
    } finally {
      setCandidatesLoading(false);
    }
  };


  const fetchActiveCVs = async () => {
    try {
      const response = await fetch(`${BaseUrl}/api/job-seeker`);
      if (!response.ok) {
        throw new Error('Failed to fetch job seekers data');
      }
      const data = await response.json();

      // Filter CVs where agency_uid matches userId AND verification_status is "Verified"
      const activeCount = data.filter(cv =>
        cv.agency_uid === userId &&
        cv.verification_status === "Verified"
      ).length;

      setActiveCVs(activeCount);
    } catch (err) {
      console.error('Error fetching active CVs:', err);
      setActiveCVs(0);
    } finally {
      setActiveCVsLoading(false);
    }
  };


  // Add these share functions
  const handleShareClick = (cv) => {
    const candidateName = cv.first_name?.split('@')[0] || 'Candidate';
    const candidateAge = cv.age ? `${cv.age} years` : '';
    const nationality = cv.nationality || '';
    const jobPosition = getJobPosition(cv.job_position) || 'Domestic Worker';
    const experience = cv.total_work_experience || 'Not specified';
    const currentLocation = cv.current_country || 'Not specified';

    const shareText = `Check out ${candidateName}'s profile - ${jobPosition} | ${nationality} | ${candidateAge} | ${experience} experience | Currently in ${currentLocation}. Find more candidates on Gulf Worker.`;
    const shareUrl = `${window.location.origin}/a-agencyviewcandidates/${cv.user_id || cv.id}`;
    const fullShareContent = `${shareText} ${shareUrl}`;

    if (navigator.share) {
      navigator.share({
        title: `${candidateName} - ${jobPosition}`,
        text: shareText,
        url: shareUrl,
      }).catch((error) => console.log('Error sharing:', error));
    } else {
      setSelectedCandidate(cv);
      setShareContent(fullShareContent);
      setShareUrl(shareUrl);
      setShowShareModal(true);
    }
  };

  // Share utility functions
  const shareViaWhatsApp = (text) => {
    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
  };

  const shareViaFacebook = (url) => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareViaTwitter = (text) => {
    const encodedText = encodeURIComponent(text);
    window.open(`https://twitter.com/intent/tweet?text=${encodedText}`, '_blank');
  };

  const shareViaLinkedIn = (url) => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'Link copied to clipboard',
        timer: 1500,
        showConfirmButton: false
      });
    });
  };

  // Add these state variables after other state variables
  const [employerResponsePercentage, setEmployerResponsePercentage] = useState(0);
  const [employerResponseLoading, setEmployerResponseLoading] = useState(true);

  // Add this function to calculate employer response percentage
  const fetchEmployerResponsePercentage = async () => {
    try {
      // Fetch viewed profiles
      const viewedProfilesResponse = await fetch(`${BaseUrl}/api/viewed-profiles`);
      if (!viewedProfilesResponse.ok) {
        throw new Error('Failed to fetch viewed profiles');
      }
      const viewedProfilesData = await viewedProfilesResponse.json();

      // Get viewed profiles data (extract from the API response structure)
      const viewedProfiles = viewedProfilesData.success ? viewedProfilesData.data : [];

      // Fetch all users
      const usersResponse = await fetch(`${BaseUrl}/`);
      if (!usersResponse.ok) {
        throw new Error('Failed to fetch users data');
      }
      const usersData = await usersResponse.json();

      // Get logged-in agency ID
      const agencyId = loggedInUser?.id;

      // Find all users that belong to this agency
      const agencyUsers = usersData.filter(user =>
        user.agency_uid === agencyId
      );

      // Get agency user IDs
      const agencyUserIds = agencyUsers.map(user => user.id);

      // Count how many unique agency users have been viewed
      const viewedAgencyUsers = viewedProfiles.filter(item =>
        agencyUserIds.includes(item.candidate_user_id)
      );

      // Get unique candidate_user_ids (to count unique users viewed)
      const uniqueViewedUserIds = [...new Set(viewedAgencyUsers.map(item => item.candidate_user_id))];

      // Calculate percentage
      const totalAgencyCandidates = agencyUsers.length;
      const viewedCount = uniqueViewedUserIds.length;

      let percentage = 0;
      if (totalAgencyCandidates > 0) {
        percentage = (viewedCount / totalAgencyCandidates) * 100;
      }

      // Format to 1 decimal place
      setEmployerResponsePercentage(parseFloat(percentage.toFixed(1)));

    } catch (err) {
      console.error('Error fetching employer response data:', err);
      setEmployerResponsePercentage(0);
    } finally {
      setEmployerResponseLoading(false);
    }
  };







  // Get logged-in user from localStorage
  const loggedInUser = JSON.parse(localStorage.getItem('user'));
  const userId = loggedInUser?.id;

  useEffect(() => {
    fetchAgencyData();
    fetchTotalCandidates(); // Add this
    fetchActiveCVs(); // Add this line
    fetchActiveJobs(); // Add this
    fetchEmployerResponsePercentage(); // Add this
    fetchDualSubscriptions();
    fetchWalletInfo();
    fetchProfileViewers();
  }, []);


  const fetchDualSubscriptions = async () => {
    try {
      const pRes = await fetch(`${BaseUrl}/api/agency/subscription/${userId}?category=posting`);
      if (pRes.ok) {
        const pData = await pRes.json();
        setPostingSub(pData.subscription);
      }
      const cRes = await fetch(`${BaseUrl}/api/agency/subscription/${userId}?category=contact`);
      if (cRes.ok) {
        const cData = await cRes.json();
        setContactSub(cData.subscription);
      }
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    }
  };

  const fetchWalletInfo = async () => {
    try {
      const wRes = await fetch(`${BaseUrl}/api/agency/wallet/${userId}`);
      if (wRes.ok) {
        const wData = await wRes.json();
        setWalletBalance(wData.balance);
      }
      const tRes = await fetch(`${BaseUrl}/api/agency/wallet/transactions/${userId}`);
      if (tRes.ok) {
        const tData = await tRes.json();
        setTransactions(tData.transactions || []);
      }
    } catch (err) {
      console.error('Error fetching wallet info:', err);
    }
  };

  const handleTopUp = async () => {
    if (!topUpAmount || isNaN(topUpAmount) || topUpAmount <= 0) {
      Swal.fire('Error', 'Please enter a valid amount', 'error');
      return;
    }

    setProcessingTopUp(true);
    try {
      const res = await fetch(`${BaseUrl}/api/agency/wallet/topup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agency_id: userId,
          amount: parseFloat(topUpAmount),
          description: 'Wallet Top-up'
        })
      });

      if (res.ok) {
        Swal.fire('Success', 'Wallet topped up successfully', 'success');
        setTopUpModal(false);
        setTopUpAmount('');
        fetchWalletInfo();
      } else {
        const error = await res.json();
        Swal.fire('Error', error.error || 'Top-up failed', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error', 'error');
    } finally {
      setProcessingTopUp(false);
    }
  };

  const fetchAgencyData = async () => {
    try {
      const response = await fetch(`${BaseUrl}/api/agency`);
      if (!response.ok) {
        throw new Error('Failed to fetch agency data');
      }
      const data = await response.json();

      // Find the agency that matches the logged-in user ID
      const userAgency = data.find(agency => agency.id === userId);

      if (userAgency) {
        setAgencyData(userAgency);
      } else {
        setError('Agency data not found');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add this useEffect after the existing one
  useEffect(() => {
    if (agencyData) {
      fetchRecentCVs();
    }
  }, [agencyData]);


  const fetchSubscriptionPlanDetails = async (planName) => {
    try {
      const response = await fetch(`${BaseUrl}/api/subscriptionplans`);
      if (!response.ok) throw new Error("Failed to fetch plans");

      const plans = await response.json();

      const matchedPlan = plans.find(
        (plan) => plan.plan_name?.toLowerCase() === planName?.toLowerCase()
      );

      if (matchedPlan) {
        setPlanDetails(matchedPlan);
      }
    } catch (err) {
      console.error("Subscription plan error:", err);
    }
  };


  useEffect(() => {
    if (agencyData?.plan_name) {
      fetchSubscriptionPlanDetails(agencyData.plan_name);
    }
  }, [agencyData]);



  const fetchProfileViewers = async () => {
    try {
      if (!userId) return;
      const response = await fetch(`${BaseUrl}/api/profile-viewers/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setProfileViewers(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching profile viewers:', err);
    } finally {
      setViewersLoading(false);
    }
  };


  const fetchRecentCVs = async () => {
    try {
      const response = await fetch(`${BaseUrl}/api/job-seeker`);
      if (!response.ok) {
        throw new Error('Failed to fetch CV data');
      }
      const data = await response.json();

      // Filter CVs by agency_uid matching the logged-in agency ID
      const agencyCVs = data.filter(cv => cv.agency_uid === userId);

      // Sort by created_at (most recent first) and take only the first 4
      const sortedCVs = agencyCVs
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 4);

      setRecentCVs(sortedCVs);
    } catch (err) {
      setCvError(err.message);
    } finally {
      setCvLoading(false);
    }
  };


  // Add these helper functions after existing ones
  const formatTimestamp = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const isPremiumPlan = () => {
    const planName = postingSub?.plan_name?.toLowerCase() || "";
    return planName.includes('silver') || planName.includes('gold');
  };

  const handleLockedFeatureClick = () => {
    navigate('/a-jobsubscriptionplans');
  };
  const getJobPosition = (jobPosition) => {
    if (!jobPosition) return 'N/A';
    try {
      const positions = JSON.parse(jobPosition);
      return Array.isArray(positions) ? positions[0] : positions;
    } catch {
      return jobPosition;
    }
  };

  const getProfilePhoto = (photoPath) => {
    if (!photoPath) return null;

    // already full URL
    if (photoPath.startsWith("http")) {
      return photoPath;
    }

    // relative path from API
    return `${BaseUrl}${photoPath}`;
  };


  const isPlanActive = (startDate, endDate) => {
    if (!startDate || !endDate) return false;

    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    return now >= start && now <= end;
  };


  const isCandidatePlanActive = (startDate, endDate, paymentStatus) => {
    if (!startDate || !endDate) return false;
    if (paymentStatus !== "Paid") return false;

    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    return now >= start && now <= end;
  };




  // Calculate remaining days for subscription
  const calculateRemainingDays = (endDate) => {
    if (!endDate) return 'N/A';
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? `${diffDays} Days` : 'Expired';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get subscription status color
  const getStatusColor = (endDate, paymentStatus) => {
    if (!endDate || !paymentStatus) return '#6b7280';

    const end = new Date(endDate);
    const now = new Date();

    if (paymentStatus === 'Paid' && end > now) {
      return '#10b981'; // Green for active
    } else if (paymentStatus === 'Paid' && end <= now) {
      return '#f59e0b'; // Orange for expired but paid
    } else {
      return '#ef4444'; // Red for unpaid
    }
  };

  if (loading) {
    return (
      <div className="gulfworker-main-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gulfworker-main-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="alert alert-danger" role="alert">
          Error: {error}
        </div>
      </div>
    );
  }


  return (
    <div className="new-dashboard-wrapper">
      <AgencyNavbar />
      <style>{`
        .new-dashboard-wrapper {
          background-color: #f0f2f5;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #1a1f36;
          padding-bottom: 2rem;
        }
        .dashboard-content {
          padding: 1.5rem 30px;
          width: 100%;
          margin-top: 60px; /* Space for fixed navbar */
        }

        /* Stats Row */
        .stats-row {
          margin-top: 5px;
          margin-bottom: 24px;
        }

        .stat-card {
          border: none;
          border-radius: 12px;
          padding: 15px 18px;
          color: white;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          height: auto;
          min-height: 100px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .stat-card.gold { background: linear-gradient(135deg, #f6d365 0%, #fda085 100%); }
        .stat-card.blue { background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%); }
        .stat-card.green { background: linear-gradient(135deg, #0ba360 0%, #3cba92 100%); }
        .stat-card.purple { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }

        .stat-title {
          font-size: 0.75rem;
          font-weight: 600;
          opacity: 0.95;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 1.6rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 4px;
        }

        .stat-footer {
          font-size: 0.7rem;
          display: flex;
          align-items: center;
          gap: 4px;
          opacity: 0.85;
          background: rgba(255, 255, 255, 0.15);
          padding: 3px 8px;
          border-radius: 20px;
          width: fit-content;
        }

        @media (max-width: 768px) {
          .dashboard-content {
            padding: 1rem 15px;
          }
          .stat-card {
            padding: 14px 12px;
            min-height: 90px;
          }
          .stat-value {
            font-size: 1.3rem;
          }
          .action-card-mobile {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 1rem !important;
          }
          .action-card-mobile .action-btn-mobile {
            width: 100% !important;
          }
        }

        /* Section Containers */
        .dashboard-section {
          background: white;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(0, 0, 0, 0.05);
        }

        .card-header-title {
          font-size: 1.1rem;
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #3d6e6e;
          margin-bottom: 20px;
        }

        /* Tables */
        .applied-jobs-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 8px;
          margin-top: -8px;
        }

        .applied-jobs-table thead th {
          position: sticky;
          top: -1px;
          background-color: white !important;
          z-index: 20;
          padding: 12px 16px;
          font-size: 0.8rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: none;
        }

        .applied-jobs-table thead th:first-child,
        .applied-jobs-table tr td:first-child {
          width: 1%;
          white-space: nowrap;
          padding-right: 8px; /* Further reduced padding */
        }

        .applied-jobs-table td {
          padding: 16px;
          background-color: #f8fafc;
          border: none;
          vertical-align: middle;
        }

        .applied-jobs-table tr td:first-child {
          border-top-left-radius: 12px;
          border-bottom-left-radius: 12px;
        }

        .applied-jobs-table tr td:last-child {
          border-top-right-radius: 12px;
          border-bottom-right-radius: 12px;
        }

        .btn-icon-sm {
          display: flex; /* Back to flex but with fixed constraints */
          align-items: center;
          justify-content: center;
          width: 38px !important;
          min-width: 38px !important;
          max-width: 38px !important; /* Force square shape */
          height: 38px !important;
          border-radius: 10px !important;
          text-decoration: none;
          transition: all 0.2s;
          border: 1px solid transparent;
          flex-shrink: 0;
        }

        .btn-icon-sm svg {
          display: block;
          margin: 0 auto;
          color: inherit;
          stroke: currentColor !important;
          width: 18px !important;
          height: 18px !important;
        }

        .email-btn { background-color: #e3f2fd; color: #2196f3; border-color: #bbdefb; }
        .email-btn:hover { background-color: #2196f3; color: white; }
        .agency-phone-btn { background-color: #e8f5e9; color: #168a3a; border-color: #c8e6c9; cursor: pointer; }
        .agency-phone-btn svg { color: #168a3a !important; stroke: #168a3a !important; stroke-width: 2.4; }
        .agency-phone-btn:hover { background-color: #168a3a; color: white; }
        .agency-phone-btn:hover svg { color: white !important; stroke: white !important; }
        .view-btn { background-color: #f3e5f5; color: #9c27b0; border-color: #e1beef; }
        .view-btn:hover { background-color: #9c27b0; color: white; }

        /* Plan Cards */
        .plan-card {
           border-radius: 12px;
           padding: 15px;
           color: white;
           margin-bottom: 12px;
           border: none;
           position: relative;
           min-height: 85px;
           display: flex;
           flex-direction: column;
           justify-content: center;
           box-shadow: 0 4px 10px rgba(0,0,0,0.1);
        }
        .plan-gold { background: linear-gradient(135deg, #d4a017 0%, #b8860b 100%); }
        .plan-blue { background: linear-gradient(135deg, #0061f2 0%, #0046ae 100%); }
        
        .action-card {
           background: white;
           border-radius: 16px;
           padding: 24px;
           display: flex;
           align-items: center;
           justify-content: space-between;
           border: 1px solid rgba(0, 0, 0, 0.05);
           box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
           transition: all 0.3s ease;
        }
        .action-card:hover {
           transform: translateY(-3px);
           box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }

        .post-btn {
          padding: 12px 24px;
          border-radius: 10px;
          font-weight: 600;
          border: none;
          color: white;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.2s;
          font-size: 0.95rem;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .post-btn-primary { background-color: #007bff; }
        .post-btn-primary:hover { background-color: #0046ae; box-shadow: 0 6px 16px rgba(0,123,255,0.3); }
        .post-btn-success { background-color: #20b387; }
        .post-btn-success:hover { background-color: #1a8e6b; box-shadow: 0 6px 16px rgba(32,179,135,0.3); }

        .locked-overlay-container {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
        }
      `}</style>

      <div className="dashboard-content">
        {/* Top Stats Row */}
        <Row className="stats-row g-3">
          <Col xs={6} lg={3}>
            <div className="stat-card blue">
              <div className="stat-title">Total Candidates</div>
              <div className="stat-value">
                {candidatesLoading ? <Spinner size="sm" animation="border" /> : totalCandidates}
              </div>
              <div className="stat-footer">
                <Users size={14} /> Agency Database
              </div>
            </div>
          </Col>
          <Col xs={6} lg={3}>
            <div className="stat-card gold">
              <div className="stat-title">Active Candidate</div>
              <div className="stat-value">
                {activeCVsLoading ? <Spinner size="sm" animation="border" /> : activeCVs}
              </div>
              <div className="stat-footer">
                <CheckCircle size={14} /> Verified Profiles
              </div>
            </div>
          </Col>
          <Col xs={6} lg={3}>
            <div className="stat-card purple">
              <div className="stat-title">Active Jobs</div>
              <div className="stat-value">
                {activeJobsLoading ? <Spinner size="sm" animation="border" /> : activeJobs}
              </div>
              <div className="stat-footer">
                <Briefcase size={14} /> Live Openings
              </div>
            </div>
          </Col>
          <Col xs={6} lg={3}>
            <div className="stat-card green">
              <div className="stat-title">Response Rate</div>
              <div className="stat-value">
                {employerResponseLoading ? <Spinner size="sm" animation="border" /> : `${employerResponsePercentage}%`}
              </div>
              <div className="stat-footer">
                <TrendingUp size={14} /> Employer Interaction
              </div>
            </div>
          </Col>
        </Row>

        {/* Second Row: Plans & Quick Actions */}
        <Row className="g-4 mb-4">
          <Col lg={4}>
            <div className="dashboard-section h-100">
              <h4 className="card-header-title"><Star size={20} /> Subscriptions</h4>

              <div className={`plan-card plan-gold ${postingSub ? '' : 'opacity-75'}`}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div className="d-flex align-items-center gap-2">
                    <Star size={18} fill="currentColor" />
                    <h6 className="m-0 fw-bold">{postingSub ? postingSub.plan_name : "No Posting Plan"}</h6>
                  </div>
                  <Badge bg="light" text="dark">Posting</Badge>
                </div>
                <div className="small opacity-90">
                  {postingSub ? `Expires: ${formatDate(postingSub.expiry_date)}` : "Subscribe to start posting"}
                </div>
                {!postingSub && (
                  <Button variant="light" size="sm" className="mt-2 fw-bold w-100" onClick={() => navigate('/a-jobsubscriptionplans')}>Explore Plans</Button>
                )}
              </div>

              <div className={`plan-card plan-blue ${contactSub ? '' : 'opacity-75'}`}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div className="d-flex align-items-center gap-2">
                    <TrendingUp size={18} />
                    <h6 className="m-0 fw-bold">{contactSub ? contactSub.plan_name : "No Contact Plan"}</h6>
                  </div>
                  <Badge bg="light" text="dark">Contact</Badge>
                </div>
                <div className="small opacity-90">
                  {contactSub ? `Expires: ${formatDate(contactSub.expiry_date)}` : "Subscribe to view contacts"}
                </div>
                {!contactSub && (
                  <Button variant="light" size="sm" className="mt-2 fw-bold w-100" onClick={() => navigate('/a-subscriptionplans')}>Explore Plans</Button>
                )}
              </div>
            </div>
          </Col>

          <Col lg={8}>
            <div className="d-flex flex-column gap-3 h-100">
              <div className="action-card action-card-mobile h-100">
                <div className="pe-3">
                  <h4 className="fw-bold text-dark mb-2">Post New Candidate</h4>
                  <p className="text-muted m-0 small">Instantly upload and verify a new CV to your agency database.</p>
                </div>
                <button className="post-btn post-btn-primary action-btn-mobile" onClick={() => navigate('/addjobseeker')}>
                  <User size={20} /> Post Candidate
                </button>
              </div>

              <div className="action-card action-card-mobile h-100">
                <div className="pe-3">
                  <h4 className="fw-bold text-dark mb-2">Post New Job</h4>
                  <p className="text-muted m-0 small">Create a professional job offer and start receiving applications.</p>
                </div>
                <button className="post-btn post-btn-success action-btn-mobile" onClick={() => navigate('/a-jobposting')}>
                  <Briefcase size={20} /> Post Job
                </button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Third Row: Profile Viewers */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="dashboard-section">
              <div className="section-header d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-header-title"><Eye size={22} /> See Who Viewed Your Profile</h4>
                {!isPremiumPlan() && (
                  <Badge bg="warning" text="dark" className="ms-2">Premium Feature</Badge>
                )}
              </div>

              <div className="viewers-section" style={{ position: 'relative' }}>
                {!isPremiumPlan() ? (
                  <div 
                    className="locked-overlay-container" 
                    onClick={() => handleLockedFeatureClick('who viewed your profile')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="table-responsive" style={{ filter: 'blur(5px)', opacity: 0.4, pointerEvents: 'none' }}>
                      <table className="applied-jobs-table">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Viewer Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>User Type</th>
                            <th>Viewed</th>
                            <th>Date & Time</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[1, 2, 3].map((_, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td className="fw-bold text-dark">Sample Viewer {i+1}</td>
                              <td><div className="btn-icon-sm email-btn"><Mail size={18} /></div></td>
                              <td><div className="btn-icon-sm agency-phone-btn"><Phone size={18} />
</div></td>
                              <td><Badge bg="info">Employer</Badge></td>
                              <td><Badge bg="light" text="dark">Candidate</Badge></td>
                               <td>Jan 01, 2024</td>
                               <td><div className="btn-icon-sm view-btn"><Eye size={18} /></div></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="locked-badge-center" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 3,
                      textAlign: 'center',
                      background: 'rgba(255, 255, 255, 0.95)',
                      padding: '30px',
                      borderRadius: '20px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                      border: '1px solid #eee',
                      minWidth: '280px'
                    }}>
                      <Lock size={40} className="text-warning mb-3" />
                      <h5 className="fw-bold mb-2">Feature Locked</h5>
                      <p className="text-muted small mb-4">Upgrade to Silver (Contact) or Gold (Posting) to see full viewer details.</p>
                      <Button variant="primary" className="fw-bold px-4 rounded-pill" onClick={(e) => { e.stopPropagation(); handleLockedFeatureClick(); }}>Upgrade Now</Button>
                    </div>
                  </div>
                ) : viewersLoading ? (
                  <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                ) : profileViewers.length > 0 ? (
                  <div className="table-responsive">
                    <table className="applied-jobs-table">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Viewer Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>User Type</th>
                        <th>Viewed</th>
                        <th>Date & Time</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profileViewers.map((viewer, index) => (
                        <tr key={viewer.id}>
                          <td>{index + 1}</td>
                          <td className="fw-bold text-dark">{viewer.viewer_name_resolved || viewer.viewer_name || 'Anonymous'}</td>
                          <td>
                            {(viewer.viewer_email_resolved || viewer.viewer_email) ? (
                              <div 
                                className="btn-icon-sm email-btn" 
                                title={viewer.viewer_email_resolved || viewer.viewer_email} 
                                onClick={() => window.location.href = `mailto:${viewer.viewer_email_resolved || viewer.viewer_email}`}
                              >
                                <Mail size={18} />
                              </div>
                            ) : '-'}
                          </td>
                          <td>
                            {(viewer.viewer_phone_resolved || viewer.viewer_phone) ? (
                              <div 
                                className="btn-icon-sm agency-phone-btn" 
                                title={`${viewer.viewer_phone_code || ''} ${viewer.viewer_phone_resolved || viewer.viewer_phone}`.trim()} 
                                onClick={() => window.location.href = `tel:${(viewer.viewer_phone_code || '')}${viewer.viewer_phone_resolved || viewer.viewer_phone}`}
                              >
                                <Phone size={18} />
                              </div>
                            ) : '-'}
                          </td>
                          <td>
                            <Badge bg="info" className="text-capitalize px-3 py-2">{viewer.viewer_user_type || 'User'}</Badge>
                          </td>
                          <td>
                            <Badge bg="light" text="dark" className="text-capitalize px-3 py-2">
                              {viewer.view_type}
                            </Badge>
                          </td>
                          <td className="small text-muted">{new Date(viewer.viewed_at).toLocaleDateString('en-GB')}<br/>{new Date(viewer.viewed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</td>
                          <td>
                            {viewer.viewer_user_type === 'job seeker' && viewer.viewer_user_id ? (
                              <div 
                                className="btn-icon-sm view-btn"
                                onClick={() => navigate(`/allcandidatesdetails/${viewer.viewer_user_id}`)}
                                style={{ cursor: 'pointer' }}
                              >
                                <Eye size={18} />
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted bg-light rounded-3">
                    <Eye size={40} className="mb-3 opacity-25" />
                    <p className="m-0">No profile views recorded yet.</p>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Top-up Modal */}
      <Modal show={topUpModal} onHide={() => setTopUpModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Top-up Wallet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Enter Amount (AED)</Form.Label>
            <Form.Control
              type="number"
              placeholder="0.00"
              value={topUpAmount}
              onChange={(e) => setTopUpAmount(e.target.value)}
            />
          </Form.Group>
          <div className="d-grid gap-2">
            <Button variant="primary" onClick={handleTopUp} disabled={processingTopUp}>
              {processingTopUp ? <Spinner size="sm" animation="border" /> : 'Top-up Now'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Share Modal */}
      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Share {selectedCandidate ? `${selectedCandidate.first_name}'s Profile` : 'CV Database'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-4">Share via:</p>
          <div className="d-flex justify-content-center gap-3 mb-4">
            <Button variant="success" className="rounded-circle" style={{ width: '45px', height: '45px' }} onClick={() => shareViaWhatsApp(shareContent)}>
              <i className="bi bi-whatsapp"></i>
            </Button>
            <Button variant="primary" className="rounded-circle" style={{ width: '45px', height: '45px' }} onClick={() => shareViaFacebook(shareUrl)}>
              <i className="bi bi-facebook"></i>
            </Button>
            <Button variant="info" className="rounded-circle text-white" style={{ width: '45px', height: '45px' }} onClick={() => shareViaTwitter(shareContent)}>
              <i className="bi bi-twitter"></i>
            </Button>
            <Button variant="primary" style={{ backgroundColor: '#0077b5', width: '45px', height: '45px' }} className="rounded-circle" onClick={() => shareViaLinkedIn(shareUrl)}>
              <i className="bi bi-linkedin"></i>
            </Button>
            <Button variant="secondary" className="rounded-circle" style={{ width: '45px', height: '45px' }} onClick={() => copyToClipboard(shareUrl)}>
              <i className="bi bi-link-45deg"></i>
            </Button>
          </div>
          <Form.Group>
            <Form.Label>Share this text:</Form.Label>
            <Form.Control as="textarea" rows={3} value={shareContent} readOnly onClick={(e) => e.target.select()} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowShareModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
      <Footer />
    </div>
  );
}
